/**
 * Voucher wallet DEV-ONLY harness — a localStorage-backed fixture set that the
 * real vouchers store reads instead of the API, so every card and every detail
 * branch can be checked without a backend, a partner token, or an assigned
 * voucher.
 *
 * Same shape as src/utils/next-steps-mock.js and src/utils/egiro-harness.js: a
 * toggle plus a state blob, gated on non-production in both this file and the
 * store that consumes it.
 *
 * Why fixtures rather than a seeded dev database: the states that matter most
 * are the ones that are hard to arrange for real — a cancelled partner order, a
 * pass that expired yesterday, the third pass of a three-book order. Those take
 * minutes to click through here and a support conversation to arrange live.
 */

export const VOUCHER_HARNESS_KEY = 'voucherHarnessState';

const isProd = () => process.env.VUE_APP_NODE_ENV === 'production';

// ── Time helpers ─────────────────────────────────────────────────────────────
// Fixtures are generated relative to now, so "expires tomorrow" stays true
// however long this file sits in the repo.
const daysFromNow = days => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
};

export const CATEGORIES = [
  { id: 'cat-collection', name: 'Collection' },
  { id: 'cat-cafe', name: 'Cafe' },
  { id: 'cat-course', name: 'Courses' },
];

/**
 * Every distinct state the wallet can render, one fixture each.
 *
 * `exercises` is shown in the console so it is obvious what each row is for —
 * and so a reviewer can tell at a glance whether a state has been covered.
 */
export const FIXTURES = [
  // ── Partner collection passes (IDR mints, their counter burns) ────────────
  {
    key: 'partner-available',
    exercises: 'Partner pass ready to collect — shows the QR, the code in words, and Copy',
    voucher: {
      id: 'partner:IDRC-7K3M-9QRS-2XYZ',
      name: 'Rooted · Paperback',
      description: 'Collect at the IDR counter.',
      categoryId: 'cat-collection',
      categoryName: 'Collection',
      value: 'S$5.00',
      status: 'available',
      expiresAt: null,
      redeemedAt: null,
      imageUrl: null,
      unlocksProgramSlug: null,
      source: 'partner',
      collectionCode: 'IDRC-7K3M-9QRS-2XYZ',
    },
  },
  {
    key: 'partner-collected',
    exercises: 'Collected at the counter — the state "Check if collected" flips it into',
    voucher: {
      id: 'partner:IDRC-2D9F-7GHM-3PQR',
      name: 'Rooted · Hardback',
      description: null,
      categoryId: 'cat-collection',
      categoryName: 'Collection',
      value: 'S$12.00',
      status: 'redeemed',
      expiresAt: null,
      redeemedAt: daysFromNow(-2),
      imageUrl: null,
      unlocksProgramSlug: null,
      source: 'partner',
      collectionCode: 'IDRC-2D9F-7GHM-3PQR',
    },
  },
  {
    key: 'partner-available-course',
    exercises: 'Partner pass that also unlocks a course, still to collect — QR only, never Redeem',
    voucher: {
      id: 'partner:IDRC-5H8J-2KMN-4PQR',
      name: 'Rooted · Course edition',
      description: 'Collect at the IDR counter. Unlocks Next Steps Part 1.',
      categoryId: 'cat-collection',
      categoryName: 'Collection',
      value: 'S$18.00',
      status: 'available',
      expiresAt: null,
      redeemedAt: null,
      imageUrl: null,
      unlocksProgramSlug: 'next-steps-p1',
      source: 'partner',
      collectionCode: 'IDRC-5H8J-2KMN-4PQR',
    },
  },
  {
    key: 'partner-collected-course',
    exercises:
      'Collected partner pass that also unlocks a course — Collected copy plus Go to course',
    voucher: {
      id: 'partner:IDRC-K7M2-9XPQ-3TRN',
      name: 'Rooted · Course edition',
      description: null,
      categoryId: 'cat-collection',
      categoryName: 'Collection',
      value: 'S$18.00',
      status: 'redeemed',
      expiresAt: null,
      redeemedAt: daysFromNow(-1),
      imageUrl: null,
      unlocksProgramSlug: 'next-steps-p1',
      source: 'partner',
      collectionCode: 'IDRC-K7M2-9XPQ-3TRN',
    },
  },
  {
    key: 'partner-cancelled',
    exercises:
      'Cancelled partner order — stays visible on purpose, because IDR treats it as recoverable',
    voucher: {
      id: 'partner:IDRC-8M2K-4NPS-9VWX',
      name: 'Rooted · Paperback',
      description: null,
      categoryId: 'cat-collection',
      categoryName: 'Collection',
      value: 'S$5.00',
      status: 'cancelled',
      expiresAt: null,
      redeemedAt: null,
      imageUrl: null,
      unlocksProgramSlug: null,
      source: 'partner',
      collectionCode: 'IDRC-8M2K-4NPS-9VWX',
    },
  },

  {
    key: 'partner-expired',
    exercises:
      'Expired partner pass — IDR owns expiry and reports it as a status, because a ' +
      'collection pass carries no date of its own. This used to arrive as `available`, ' +
      'so the card showed a green Available tag on a code the counter refuses',
    voucher: {
      id: 'partner:IDRC-3TQ7-6HKD-2FRB',
      name: 'Grounded · Paperback',
      description: null,
      categoryId: 'cat-collection',
      categoryName: 'Collection',
      value: 'S$5.00',
      status: 'expired',
      // Deliberately null: the whole point is that the expiry is NOT a date we
      // hold. A fixture with one would prove the wrong thing.
      expiresAt: null,
      redeemedAt: null,
      imageUrl: null,
      unlocksProgramSlug: null,
      source: 'partner',
      collectionCode: 'IDRC-3TQ7-6HKD-2FRB',
    },
  },

  // ── In-app course unlocks ────────────────────────────────────────────────
  {
    key: 'course-available',
    exercises: 'Course unlock, not yet redeemed — the only voucher with a "Redeem & unlock" CTA',
    voucher: {
      id: 'v-course-1',
      name: 'Next Steps · Part 1',
      description: 'Unlocks the full course in the app.',
      categoryId: 'cat-course',
      categoryName: 'Courses',
      value: 'Full access',
      status: 'available',
      expiresAt: null,
      redeemedAt: null,
      imageUrl: null,
      unlocksProgramSlug: 'next-steps-p1',
    },
  },
  {
    key: 'course-redeemed',
    exercises: 'Course already unlocked — confirmation card plus a Go to course button',
    voucher: {
      id: 'v-course-2',
      name: 'Next Steps · Part 1',
      description: null,
      categoryId: 'cat-course',
      categoryName: 'Courses',
      value: 'Full access',
      status: 'redeemed',
      expiresAt: null,
      redeemedAt: daysFromNow(-9),
      imageUrl: null,
      unlocksProgramSlug: 'next-steps-p1',
    },
  },

  // ── Expiry states ────────────────────────────────────────────────────────
  {
    key: 'expiring-soon',
    exercises: 'Expires in 3 days — an amber "Expiring" tag, and the card counts down in words',
    voucher: {
      id: 'v-expiring',
      name: 'Cafe drink',
      description: null,
      categoryId: 'cat-cafe',
      categoryName: 'Cafe',
      value: 'One free drink',
      status: 'available',
      expiresAt: daysFromNow(3),
      redeemedAt: null,
      imageUrl: null,
      unlocksProgramSlug: null,
    },
  },
  {
    key: 'expired',
    exercises: 'Expired yesterday — still listed, but every CTA is disabled',
    voucher: {
      id: 'v-expired',
      name: 'Cafe pastry',
      description: null,
      categoryId: 'cat-cafe',
      categoryName: 'Cafe',
      value: 'One free pastry',
      status: 'available',
      expiresAt: daysFromNow(-1),
      redeemedAt: null,
      imageUrl: null,
      unlocksProgramSlug: null,
    },
  },

  // ── Presentation edge cases ──────────────────────────────────────────────
  {
    key: 'with-image',
    exercises: 'Has an image — only the detail screen renders it; the list card never does',
    voucher: {
      id: 'v-image',
      name: 'Anniversary mug',
      description: 'A mug, in celebration of nothing in particular.',
      categoryId: 'cat-cafe',
      categoryName: 'Cafe',
      value: 'Free mug',
      status: 'available',
      expiresAt: daysFromNow(30),
      redeemedAt: null,
      // Inline SVG data URI: no network, so it renders identically offline and
      // in a screenshot test.
      imageUrl:
        'data:image/svg+xml;utf8,' +
        encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240">' +
            // Plain '#' here — encodeURIComponent escapes it. Pre-escaping it as
            // %23 gets double-encoded into %2523, which is not a colour at all.
            '<rect width="240" height="240" fill="#f2c744"/>' +
            '<circle cx="120" cy="120" r="54" fill="none" stroke="#222" stroke-width="10"/>' +
            '<path d="M174 96h22a24 24 0 0 1 0 48h-22" fill="none" stroke="#222" stroke-width="10"/>' +
            '</svg>',
        ),
      unlocksProgramSlug: null,
    },
  },
  {
    key: 'long-name',
    exercises:
      'Very long name and value — proves the card truncates instead of pushing the chevron off',
    voucher: {
      id: 'v-long',
      name: 'Limited edition anniversary hardback with foil cover and signed bookplate',
      description: null,
      categoryId: 'cat-collection',
      categoryName: 'Collection',
      value: 'S$48.00 — members only',
      status: 'available',
      expiresAt: null,
      redeemedAt: null,
      imageUrl: null,
      unlocksProgramSlug: null,
      source: 'partner',
      collectionCode: 'IDRC-9XQZ-1ABC-7DEF',
    },
  },
  {
    key: 'revoked',
    exercises:
      'Revoked — must NOT appear in the wallet at all. Included to prove it is filtered out.',
    voucher: {
      id: 'v-revoked',
      name: 'Should never be visible',
      description: null,
      categoryId: 'cat-cafe',
      categoryName: 'Cafe',
      value: 'S$5.00',
      status: 'revoked',
      expiresAt: null,
      redeemedAt: null,
      imageUrl: null,
      unlocksProgramSlug: null,
    },
  },
];

/** Fixtures loaded when the harness is first switched on. */
const DEFAULT_SELECTION = FIXTURES.map(f => f.key);

const DEFAULT_STATE = {
  enabled: false,
  // Which fixture keys are in the wallet.
  selected: DEFAULT_SELECTION,
  // Forced conditions, so the loading and failure branches are reachable too.
  // 'none' | 'loading' | 'error'
  condition: 'none',
};

function read() {
  if (typeof window === 'undefined') return { ...DEFAULT_STATE };
  try {
    const raw = window.localStorage.getItem(VOUCHER_HARNESS_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const stored = { ...DEFAULT_STATE, ...JSON.parse(raw) };
    // Drop selections naming a fixture that no longer exists. Renaming or
    // splitting a fixture otherwise leaves a stored selection pointing at
    // nothing, and the wallet opens empty with no hint why.
    const known = new Set(FIXTURES.map(f => f.key));
    return { ...stored, selected: (stored.selected ?? []).filter(key => known.has(key)) };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function write(next) {
  if (typeof window === 'undefined') return next;
  try {
    window.localStorage.setItem(VOUCHER_HARNESS_KEY, JSON.stringify(next));
  } catch {
    // Private browsing or a full quota. The harness is dev-only, so degrading
    // to in-memory-for-this-page-load is fine.
  }
  return next;
}

export function getHarnessState() {
  return read();
}

export function setHarnessState(patch) {
  return write({ ...read(), ...patch });
}

export function resetHarness() {
  return write({ ...DEFAULT_STATE });
}

/**
 * True when the store should serve fixtures instead of calling the API.
 * Guarded on non-production here as well as at every call site — a harness that
 * can be switched on in production is not a harness, it is an outage.
 */
export function isVoucherHarnessEnabled() {
  return !isProd() && read().enabled === true;
}

/** The forced condition, so the store can fake a hang or a failure. */
export function harnessCondition() {
  return read().condition ?? 'none';
}

/** Vouchers currently in the fixture wallet, in the order the list should show. */
export function harnessVouchers() {
  const { selected } = read();
  return FIXTURES.filter(f => selected.includes(f.key)).map(f => ({ ...f.voucher }));
}

export function harnessVoucherById(id) {
  return harnessVouchers().find(v => v.id === id) ?? null;
}

export function harnessCategories() {
  // Only categories actually represented, so the filter row matches the list.
  const present = new Set(harnessVouchers().map(v => v.categoryId));
  return CATEGORIES.filter(c => present.has(c.id));
}

/** Marks a fixture redeemed for the rest of the session, so redeem flows land somewhere. */
export function harnessMarkRedeemed(id) {
  const fixture = FIXTURES.find(f => f.voucher.id === id);
  if (!fixture) return;
  fixture.voucher.status = 'redeemed';
  fixture.voucher.redeemedAt = new Date().toISOString();
}

/**
 * The general states of the wallet, in the order a member would meet them.
 *
 * Deliberately coarse: one step per state that behaves differently, not one per
 * fixture. The fixture list below the stepper is still there for edge cases
 * (truncation, images, multi-unit labels) — this is for walking the journey.
 *
 * `goTo` is 'wallet' for a list state, or a fixture key to open that card
 * directly, because some states only exist on the detail screen.
 */
export const FLOW_STEPS = [
  {
    key: 'empty',
    label: 'Nothing yet',
    what: 'No vouchers. What a member sees before they buy anything.',
    fixtures: [],
    goTo: 'wallet',
  },
  {
    key: 'to-collect',
    label: 'To collect',
    what: 'A book paid for and waiting at the counter — shows the IDRC code.',
    fixtures: ['partner-available'],
    goTo: 'partner-available',
  },
  {
    key: 'collected',
    label: 'Collected',
    what: 'The counter burned it. What "Check if collected" flips it into.',
    fixtures: ['partner-collected'],
    goTo: 'partner-collected',
  },
  {
    key: 'collected-unlock',
    label: 'Collected, course',
    what: 'The counter burned a pass that also unlocks a course. Go to course, not Redeem.',
    fixtures: ['partner-collected-course'],
    goTo: 'partner-collected-course',
  },
  {
    key: 'cancelled',
    label: 'Unavailable',
    what: 'The order was cancelled. Shown as unavailable, in its own tab, because IDR can restore it.',
    fixtures: ['partner-cancelled'],
    goTo: 'partner-cancelled',
  },
  {
    // Deliberately paired with the date-driven 'expired' step below: the two look
    // the same to a member and reach that state by different routes, and only
    // one of them was working.
    key: 'expired-idr',
    label: 'Expired by IDR',
    what:
      'A collection pass IDR expired. Reported as a status, not a date — the ' +
      'pass carries no expiry of its own, which is why this used to show as ' +
      'Available on a code the counter refuses.',
    fixtures: ['partner-expired'],
    goTo: 'partner-expired',
  },
  {
    key: 'unlock-ready',
    label: 'Course, ready',
    what: 'A course unlock not yet redeemed — the only Redeem button in the app.',
    fixtures: ['course-available'],
    goTo: 'course-available',
  },
  {
    key: 'unlock-done',
    label: 'Course, unlocked',
    what: 'Already redeemed. Confirmation plus a way into the course.',
    fixtures: ['course-redeemed'],
    goTo: 'course-redeemed',
  },
  {
    key: 'expired',
    label: 'Expired',
    what: 'Past a date we hold. Still listed, every action disabled.',
    fixtures: ['expired'],
    goTo: 'expired',
  },
  {
    key: 'mixed',
    label: 'A full wallet',
    what: 'Several at once, so the status filters and counts can be checked.',
    fixtures: ['partner-available', 'partner-collected', 'expiring-soon', 'course-available'],
    goTo: 'wallet',
  },
  {
    key: 'not-found',
    label: 'Not found',
    what: 'A revoked or missing voucher. Opening it must not confirm it exists.',
    fixtures: ['revoked'],
    goTo: 'revoked',
  },
  {
    key: 'failed',
    label: "Can't load",
    what: 'The API is down. Shows the retry, never an empty wallet.',
    fixtures: ['partner-available'],
    condition: 'error',
    goTo: 'wallet',
  },
];

/** Applies a flow step: swaps the fixtures and the forced condition together. */
export function applyFlowStep(step) {
  return setHarnessState({
    enabled: true,
    selected: [...step.fixtures],
    condition: step.condition ?? 'none',
  });
}

/** The route a step wants to land on. */
export function flowStepRoute(step) {
  if (step.goTo === 'wallet') return '/vouchers';
  const fixture = FIXTURES.find(f => f.key === step.goTo);
  return fixture ? `/vouchers/${encodeURIComponent(fixture.voucher.id)}` : '/vouchers';
}
